import {
  capitalizeTerm,
  contributionCents,
  cutPercentToBps,
  defaultForgeBuilderCopy,
  earningsPerOpenCents,
  FORGE_MAX_ITEMS,
  FORGE_NAME_MAX,
  FORGE_TOTAL_WEIGHT,
  type ForgeBuilderCopy,
  type ForgeItem,
  type ForgeRow,
  forgeSubmitBlocker,
  forgeSubmitKey,
  isPullRateInput,
  livePriceCents,
  parseAmount,
  sortRowsByValue,
  totalWeight,
} from "@loot-os/api-contract";
import type {
  ForgeBuilderContent,
  ForgeBuilderHeadingContent,
  ForgeDetailsContent,
  ForgeItemGroup,
  ForgeNoticeContent,
  ForgePickerContent,
  ForgePickItem,
  ForgeRatesContent,
  ForgeSummaryContent,
} from "@loot-os/design-system/templates/forge/forgeContent";
import { isLootApiErrorLike } from "@loot-os/react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useCurrency } from "../../../lib/currency.js";
import {
  forgeErrorMessage,
  forgeNameHint,
  rowsFromDetail,
  useCreateForgeBox,
  useUpdateForgeDropTable,
} from "../../../lib/forge.js";
import {
  boxQueryOptions,
  forgeImagesQueryOptions,
  forgeItemsInfiniteQueryOptions,
  lootMeQueryOptions,
  myForgeQueryOptions,
} from "../../../lib/queries.js";
import { useShopper } from "../../../lib/shopper.js";
import { useTenant } from "../../../lib/tenant.js";
import { useTerminology } from "../../../lib/terminology.js";
import { showToast } from "../../../lib/toasts.js";

/** What the edit page loads the builder with: the published version it edits. */
export interface ForgeBuilderEdit {
  boxId: string;
  name: string;
  imageUrl: string;
  version: number;
  creatorCutPercent: number;
  rows: ForgeRow[];
}

/** The username dialog the page opens over the builder: the gate's button, or a refusal. */
export interface UsernameDialogState {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The builder's heading, for creating or editing; `actionBar` while the summary is showing. */
export function useForgeBuilderHeading(
  mode: "create" | "edit",
  actionBar: boolean,
): ForgeBuilderHeadingContent {
  const { heading } = defaultForgeBuilderCopy(useTerminology());
  const words = heading[mode];
  return {
    trail: [
      { label: heading.home, href: "/" },
      { label: heading.forge, href: "/forge" },
    ],
    current: words.current,
    title: words.title,
    intro: words.intro,
    back: { label: heading.forge, href: "/forge" },
    actionBar,
  };
}

/**
 * Building a box names its creator on it, so the builder asks for a username before the shopper
 * fills it in, as the chat does before a first message. The server refuses a write without one
 * (`username_required`) either way; this only saves filling in the form first. Null once the
 * builder may show; a failed read lets it through, since the server still checks.
 */
export function useUsernameGate(dialog: UsernameDialogState): ForgeNoticeContent | null {
  const { sessionToken } = useShopper();
  const { box } = useTerminology();
  const me = useQuery({ ...lootMeQueryOptions(), enabled: !!sessionToken });
  if (!sessionToken || me.isPending) return { status: "loading" };
  if (me.data && !me.data.username) {
    return {
      status: "message",
      message: `Every ${box.singular} you build shows your username as its creator. Choose one first.`,
      action: { label: "Choose a username", onClick: () => dialog.onOpenChange(true) },
    };
  }
  return null;
}

/**
 * The box the edit page edits, or why it can't: still loading, failed, archived or not the
 * shopper's. A newer published version changes `edit.version`, which remounts the builder.
 */
export function useForgeEditTarget(
  boxId: string,
): { notice: ForgeNoticeContent; edit: null } | { notice: null; edit: ForgeBuilderEdit } {
  const terms = useTerminology();
  const { box } = terms;
  const currency = useCurrency();
  const { sessionToken } = useShopper();
  const mine = useQuery({ ...myForgeQueryOptions(), enabled: !!sessionToken });
  const detail = useQuery(boxQueryOptions(boxId));
  const owned = mine.data?.boxes.find((b) => b.id === boxId);

  if (mine.isError || detail.isError) {
    const err = mine.error ?? detail.error;
    return {
      notice: {
        status: "error",
        message: forgeErrorMessage(err, { terms, currency }),
      },
      edit: null,
    };
  }
  if (mine.isPending || detail.isPending) return { notice: { status: "loading" }, edit: null };
  if (owned?.status !== "active") {
    return {
      notice: {
        status: "message",
        message: owned
          ? `Only a live ${box.singular} can be edited. Unarchive it first.`
          : `This ${box.singular} is not one of yours.`,
        action: { label: `Back to my ${box.plural}`, href: "/forge/mine" },
      },
      edit: null,
    };
  }
  return {
    notice: null,
    edit: {
      boxId,
      name: owned.name,
      imageUrl: owned.imageUrl,
      version: detail.data.version,
      creatorCutPercent: owned.creatorCutPercent,
      rows: rowsFromDetail(detail.data),
    },
  };
}

/** The item search's own state: a debounced name, a value range in the store's units, a group. */
function useItemSearch() {
  const currency = useCurrency();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [minText, setMinText] = useState("");
  const [maxText, setMaxText] = useState("");
  const [group, setGroup] = useState<ForgeItemGroup>("all");

  // Type, then search: one request per pause rather than one per key.
  useEffect(() => {
    const timer = setTimeout(() => setQ(text), 300);
    return () => clearTimeout(timer);
  }, [text]);

  const minCents = minText.trim() ? parseAmount(minText, currency) : null;
  const maxCents = maxText.trim() ? parseAmount(maxText, currency) : null;
  const minInvalid = minText.trim() !== "" && minCents === null;
  const maxInvalid = maxText.trim() !== "" && maxCents === null;
  return {
    text,
    setText,
    q,
    minText,
    setMinText,
    maxText,
    setMaxText,
    minCents,
    maxCents,
    minInvalid,
    maxInvalid,
    rangeInvalid: minInvalid || maxInvalid,
    group,
    setGroup,
  };
}

/**
 * The item picker: the catalog search (`GET /v1/forge/items`), the Mystery Bulk Card tiers the
 * first page leads with, and a toggle per item.
 */
function usePickerContent(
  selected: ReadonlySet<string>,
  onToggle: (item: ForgeItem) => void,
  pickedCount: number,
  copy: ForgeBuilderCopy["picker"],
): ForgePickerContent {
  const terms = useTerminology();
  const currency = useCurrency();
  const search = useItemSearch();
  const items = useInfiniteQuery({
    ...forgeItemsInfiniteQueryOptions({
      q: search.q,
      minCents: search.minCents ?? undefined,
      maxCents: search.maxCents ?? undefined,
    }),
    enabled: !search.rangeInvalid,
  });
  const all = useMemo(() => items.data?.pages.flatMap((page) => page.data) ?? [], [items.data]);

  const toPick = (item: ForgeItem): ForgePickItem => ({
    id: item.id,
    name: item.name,
    image: item.imageUrl,
    value: item.valueCents,
    selected: selected.has(item.id),
    onToggle: () => onToggle(item),
  });
  const bulk = all.filter((item) => item.mysteryBulkCard).map(toPick);
  const rest = all.filter((item) => !item.mysteryBulkCard).map(toPick);
  const groups = [
    ...(bulk.length > 0 ? [{ title: copy.groups.bulk, items: bulk }] : []),
    ...(search.group === "all" && rest.length > 0
      ? [{ title: bulk.length > 0 ? copy.itemsTitle : undefined, items: rest }]
      : []),
  ];
  const nothing = search.group === "bulk" ? bulk.length === 0 : all.length === 0;

  return {
    step: copy.step,
    title: copy.title,
    count: copy.count(pickedCount),
    currency,
    search: {
      value: search.text,
      label: copy.search,
      placeholder: copy.search,
      onChange: search.setText,
    },
    min: {
      value: search.minText,
      label: copy.min.label,
      placeholder: copy.min.placeholder,
      invalid: search.minInvalid,
      onChange: search.setMinText,
    },
    max: {
      value: search.maxText,
      label: copy.max.label,
      placeholder: copy.max.placeholder,
      invalid: search.maxInvalid,
      onChange: search.setMaxText,
    },
    group: {
      value: search.group,
      label: copy.groupLabel,
      options: [
        { value: "all", label: copy.groups.all },
        { value: "bulk", label: copy.groups.bulk },
      ],
      onValueChange: search.setGroup,
    },
    rangeError: search.rangeInvalid ? "Enter the values as amounts." : null,
    error: items.isError ? forgeErrorMessage(items.error, { terms, currency }) : null,
    state: search.rangeInvalid
      ? { status: "idle" }
      : items.isPending
        ? { status: "loading" }
        : {
            status: "ready",
            groups,
            empty: items.isSuccess && nothing ? copy.empty : null,
          },
    more:
      search.group === "all" && items.hasNextPage
        ? {
            shown: all.length,
            loading: items.isFetchingNextPage,
            label: copy.loadMore,
            onLoadMore: () => void items.fetchNextPage(),
          }
        : null,
  };
}

/**
 * The forge builder, boxed's five steps on one page: name and image, the items, their pull rates,
 * then the cut and the price. Editing (`edit` set) locks the name and image and sends the whole
 * table again, pinned to the version it loaded. Saves and refusals are toasts; a refusal for a
 * missing username opens the username dialog, and a version that moved on asks the page to reload.
 */
export function useForgeBuilderContent({
  edit,
  onVersionChanged,
}: {
  edit?: ForgeBuilderEdit;
  /** Editing only: the box moved on under the builder; the page reloads it. */
  onVersionChanged?: () => void;
} = {}): { content: ForgeBuilderContent; usernameDialog: UsernameDialogState } {
  const terms = useTerminology();
  const copy = defaultForgeBuilderCopy(terms);
  const currency = useCurrency();
  const { config } = useTenant();
  const navigate = useNavigate();
  const forge = config.forge;
  const commission = forge?.commissionEnabled ?? false;
  const maxCut = forge?.maxCreatorCutPercent ?? 0;
  const Box = capitalizeTerm(terms.box.singular);

  const images = useQuery({ ...forgeImagesQueryOptions(), enabled: !edit });
  const imageList = images.data?.images ?? [];

  const [name, setName] = useState(edit?.name ?? "");
  const [imageUrl, setImageUrl] = useState(edit?.imageUrl ?? "");
  const [rows, setRows] = useState<ForgeRow[]>(edit?.rows ?? []);
  const [cutPercent, setCutPercent] = useState(() =>
    commission ? Math.min(edit?.creatorCutPercent ?? 0, maxCut) : 0,
  );
  const [usernameOpen, setUsernameOpen] = useState(false);

  const create = useCreateForgeBox();
  const update = useUpdateForgeDropTable(edit?.boxId ?? "");
  const pending = create.isPending || update.isPending;

  const sentCut = commission ? cutPercent : 0;
  const cutBps = cutPercentToBps(sentCut);
  const priceCents = livePriceCents(rows, cutBps);
  const total = totalWeight(rows);
  const state = {
    mode: edit ? ("edit" as const) : ("create" as const),
    name,
    imageUrl,
    imagesOffered: imageList.length,
    rows,
  };
  const blocker = forgeSubmitBlocker(state);
  const selected = useMemo(() => new Set(rows.map((row) => row.id)), [rows]);

  function toggleItem(item: ForgeItem) {
    if (selected.has(item.id)) {
      setRows((prev) => prev.filter((row) => row.id !== item.id));
      return;
    }
    if (rows.length >= FORGE_MAX_ITEMS) {
      showToast({
        kind: "error",
        title: `Up to ${FORGE_MAX_ITEMS} ${terms.item.plural} per ${terms.box.singular}.`,
      });
      return;
    }
    setRows((prev) => [
      ...prev,
      {
        id: item.id,
        name: item.name,
        imageUrl: item.imageUrl,
        valueCents: item.valueCents,
        percent: "",
      },
    ]);
  }

  function setPercent(id: string, text: string) {
    if (!isPullRateInput(text)) return;
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, percent: text } : row)));
  }

  function submit() {
    if (blocker !== null || pending) return;
    const onError = (err: unknown) => {
      showToast({
        kind: "error",
        title: forgeErrorMessage(err, {
          terms,
          currency,
          itemName: (id) => rows.find((row) => row.id === id)?.name,
        }),
      });
      if (!isLootApiErrorLike(err)) return;
      const code: string = err.code;
      if (code === "username_required") setUsernameOpen(true);
      if (code === "box_version_changed") onVersionChanged?.();
    };
    if (edit) {
      update.mutate(
        { creatorCutPercent: sentCut, rows, expectedVersion: edit.version },
        {
          onSuccess: () => {
            showToast({ kind: "success", title: `${Box} saved.` });
            void navigate({ to: "/forge/mine" });
          },
          onError,
        },
      );
      return;
    }
    create.mutate(
      { name, imageUrl: imageList.length > 0 ? imageUrl : "", creatorCutPercent: sentCut, rows },
      {
        onSuccess: (res) => {
          showToast({ kind: "success", title: `${Box} published.` });
          void navigate({ to: "/boxes/$boxId", params: { boxId: res.box.id } });
        },
        onError,
      },
    );
  }

  const nameHint = name.trim() ? forgeNameHint(name) : null;
  const details: ForgeDetailsContent = {
    step: copy.details.step,
    title:
      images.isPending || imageList.length > 0 || edit?.imageUrl
        ? copy.details.title.withImage
        : copy.details.title.nameOnly,
    name: edit
      ? { kind: "fixed", label: copy.details.nameLabel, value: edit.name }
      : {
          kind: "field",
          label: copy.details.nameLabel,
          value: name,
          placeholder: copy.details.namePlaceholder,
          maxLength: FORGE_NAME_MAX,
          helper: copy.details.nameHelper,
          error: nameHint,
          onChange: setName,
        },
    image: edit
      ? edit.imageUrl
        ? { kind: "fixed", label: copy.details.imageLabel, src: edit.imageUrl }
        : null
      : images.isPending
        ? { kind: "loading", label: copy.details.imageLabel }
        : imageList.length > 0
          ? {
              kind: "choose",
              label: copy.details.imageLabel,
              value: imageUrl,
              onSelect: setImageUrl,
              options: imageList.map((src, i) => ({ src, label: copy.details.imageOption(i + 1) })),
            }
          : null,
  };

  const rates: ForgeRatesContent = {
    step: copy.rates.step,
    title: copy.rates.title,
    description: copy.rates.description,
    currency,
    total: {
      text: `${(total / 10_000).toFixed(3)}%`,
      label: copy.rates.totalLabel,
      tone: total === FORGE_TOTAL_WEIGHT ? "complete" : total === 0 ? "empty" : "off",
    },
    empty: copy.rates.empty,
    sort: {
      label: copy.rates.sortLabel,
      ascending: {
        label: copy.rates.ascending,
        onClick: () => setRows((p) => sortRowsByValue(p, "asc")),
      },
      descending: {
        label: copy.rates.descending,
        onClick: () => setRows((p) => sortRowsByValue(p, "desc")),
      },
    },
    rows: rows.map((row) => ({
      id: row.id,
      name: row.name,
      image: row.imageUrl,
      value: row.valueCents,
      percent: {
        value: row.percent,
        label: copy.rates.rateLabel(row.name),
        placeholder: copy.rates.ratePlaceholder,
        onChange: (text) => setPercent(row.id, text),
      },
      contribution: contributionCents(row, cutBps),
      remove: {
        label: copy.rates.remove(row.name),
        onRemove: () => setRows((prev) => prev.filter((other) => other.id !== row.id)),
      },
    })),
  };

  const summary: ForgeSummaryContent = {
    step: copy.summary.step,
    title: copy.summary.title[edit ? "edit" : "create"],
    currency,
    cut:
      commission && maxCut > 0
        ? {
            label: copy.summary.cutLabel,
            value: cutPercent,
            max: maxCut,
            step: 0.01,
            format: (percent) => `${percent.toFixed(2)}%`,
            onChange: setCutPercent,
            earnings: {
              before: copy.summary.earnings.before,
              cents: earningsPerOpenCents(priceCents, cutPercent),
              after: copy.summary.earnings.after,
            },
          }
        : null,
    price: { label: copy.summary.priceLabel, cents: priceCents },
    submit: {
      label: pending ? copy.summary.saving : copy.summary.submit[forgeSubmitKey(state)],
      disabled: blocker !== null || pending || (!edit && images.isPending),
      busy: pending,
      onSubmit: submit,
    },
    cancel: { label: copy.summary.cancel, href: edit ? "/forge/mine" : "/forge" },
  };

  return {
    content: {
      heading: useForgeBuilderHeading(edit ? "edit" : "create", true),
      details,
      picker: usePickerContent(selected, toggleItem, rows.length, copy.picker),
      rates,
      summary,
    },
    usernameDialog: { open: usernameOpen, onOpenChange: setUsernameOpen },
  };
}
